import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { fetchSubmissionRate } from "../../../../../utils/axios";
import HTTPService from "../../../../../utils/axios";
import "./SubmissionRateChart.css";
import { motion } from "framer-motion";

const SubmissionRateChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [widgetStats, setWidgetStats] = useState({});
  const { invokeApi } = HTTPService();
  const [stats, setStats] = useState({
    totalPrograms: 0,
    totalSubmissions: 0,
    averageSubmissionRate: 0,
  });

  const getWidgetStats = () => {
    // setResearcherPortfolioLoad(true);
    invokeApi({
      method: "GET",
      url: `widget/programs-stats/`,
    })
      .then((data) => {
        setWidgetStats(data.widget_stats);
      })
      .catch((error) => {
        // setResearcherPortfolioLoad(false);
        customToast.error("Something went wrong!", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        });
      });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchSubmissionRate();

        // Calculate average submission rate
        const totalPrograms = data.total_programs || 0;
        const totalSubmissions = data.total_submissions || 0;
        const averageSubmissionRate =
          totalPrograms > 0
            ? ((totalSubmissions / totalPrograms / 10) * 100).toFixed(0)
            : 0;

        setStats({
          totalPrograms,
          totalSubmissions,
          averageSubmissionRate,
        });

        // Create ECG-like data for the chart
        const generateECGData = () => {
          // Simplified ECG pattern that matches the reference image
          return [
            [0, 20],
            [1, 20],
            [2, 20],
            [3, 20],
            [4, 20],
            [5, 20],
            [6, 20],
            [7, 25],
            [8, 15],
            [9, 60], // spike
            [10, 10],
            [11, 20],
            [12, 20],
            [13, 20],
            [14, 20],
            [15, 20],
            [16, 20], // short tail
          ];
        };

        setChartData(generateECGData());
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch program statistics");
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
    getWidgetStats();
  }, []);

  const getOption = () => ({
    grid: {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    },
    xAxis: {
      type: "value",
      show: false,
    },
    yAxis: {
      type: "value",
      min: 0,
      max: 70,
      show: false,
    },
    series: [
      {
        type: "line",
        smooth: false,
        symbol: "none",
        sampling: "average",
        itemStyle: {
          color: "#4CD4C0", // Teal color from the reference image
        },
        lineStyle: {
          width: 2,
        },
        data: chartData,
      },
    ],
    animation: true,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 bg-white rounded-lg">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg p-4">
        <div className="text-center text-red-500">
          <p className="text-lg font-semibold">Error</p>
          <p className="mt-1">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-2">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="row text-center mt-2 mb-3">
          <div className="col">
            <div className="text-muted small">Total</div>
            <div className="fw-bold fs-6">
              {widgetStats?.total_submissions ?? 0}
            </div>
          </div>
          <div className="col border-start">
            <div className="text-muted small">Valid</div>
            <div className="fw-bold text-success fs-6">
              {widgetStats?.total_valid_submissions ?? 0}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="" style={{ height: "40px" }}>
        <ReactECharts
          option={getOption()}
          style={{ height: "80%", width: "100%" }}
          className="ecg-chart"
        />
        <p className="percent-margin">{stats.averageSubmissionRate}%</p>
      </div>
    </div>
  );
};

export default SubmissionRateChart;
